import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import { useGetDeviceQuery } from "../../Api/attoDeskApi";
import PageLoader from "../../Components/Loader/PageLoader";

/**
 * Printable device setup sheet — opens in a clean white layout with
 * no admin chrome, ready to hand to the on-site installer.
 *
 * Routed at /devices/:id/print-sheet. The Drawer + Menu treat any URL
 * starting with /devices/:id/print-sheet as a "portal" page (no
 * sidebar) — easier print preview, easier to save as PDF.
 */
const PrintSheet: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading } = useGetDeviceQuery(Number(id));

  // Auto-open the browser print dialog after data loads — matches the
  // intent ("Print Setup Sheet") without an extra click. User can
  // cancel; the page itself remains usable.
  useEffect(() => {
    if (!isLoading && data) {
      const t = setTimeout(() => { try { window.print(); } catch (_) {} }, 600);
      return () => clearTimeout(t);
    }
  }, [isLoading, data]);

  if (isLoading) return <PageLoader height="40vh" />;
  const d = (data?.data as any) || null;
  if (!d) return <Empty msg="Device not found" />;

  const origin = window.location.origin;
  const webPlayerUrl = `${origin}/player/${d.device_code}`;
  const shortUrl    = `${origin}/p/${d.device_code}`;
  const apkUrl      = `${origin}/download/android`;

  const qr = (data: string) =>
    `https://api.qrserver.com/v1/create-qr-code/?size=240x240&margin=2&data=${encodeURIComponent(data)}`;

  return (
    <div style={{
      background: "#fff", minHeight: "100vh", padding: 32,
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      color: "#0b1e2d",
    }}>
      <style>{`
        @media print {
          @page { margin: 12mm; }
          body { background: #fff !important; }
          .no-print { display: none !important; }
          .page-break { page-break-after: always; }
        }
        .print-card { border: 1px solid #e2e8f0; border-radius: 12px; padding: 24px; margin-bottom: 16px; }
        .print-row { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px dotted #cbd5e1; }
        .print-row:last-child { border-bottom: none; }
      `}</style>

      {/* Print button — hidden on print */}
      <div className="no-print" style={{
        display: "flex", justifyContent: "flex-end", marginBottom: 16,
      }}>
        <button onClick={() => window.print()} style={{
          padding: "8px 16px", borderRadius: 8, border: "1px solid #D4AF37",
          background: "#D4AF37", color: "#0b1e2d", fontWeight: 700,
          cursor: "pointer", fontSize: 14,
        }}>
          Print
        </button>
      </div>

      {/* Header */}
      <div style={{
        background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
        color: "#fff", padding: 24, borderRadius: 12, marginBottom: 24,
      }}>
        <div style={{ fontSize: 12, color: "#94a3b8", letterSpacing: "0.1em", textTransform: "uppercase" }}>
          VM Display — Device Setup Sheet
        </div>
        <div style={{ fontSize: 28, fontWeight: 800, marginTop: 6 }}>
          {d.device_name}
        </div>
        <div style={{ fontSize: 14, color: "rgba(255,255,255,0.7)", marginTop: 2 }}>
          {d.company?.name || "—"}{d.location ? ` · ${d.location}` : ""}
        </div>
      </div>

      {/* Device info */}
      <div className="print-card">
        <div style={{ fontSize: 12, fontWeight: 700, color: "#64748b",
          textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 12 }}>
          Device Identity
        </div>
        <div className="print-row">
          <span style={{ fontWeight: 600 }}>Device Code</span>
          <span style={{ fontFamily: "monospace", fontSize: 18, fontWeight: 800 }}>
            {d.device_code}
          </span>
        </div>
        <div className="print-row">
          <span style={{ fontWeight: 600 }}>Registration Code (Android)</span>
          <span style={{ fontFamily: "monospace", fontSize: 18, fontWeight: 800 }}>
            {d.device_code}
          </span>
        </div>
        <div className="print-row">
          <span style={{ fontWeight: 600 }}>Location</span>
          <span>{d.location || "—"}</span>
        </div>
        <div className="print-row">
          <span style={{ fontWeight: 600 }}>Company</span>
          <span>{d.company?.name || "—"}</span>
        </div>
      </div>

      {/* QR codes side by side */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <div className="print-card" style={{ textAlign: "center" }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#64748b",
            textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 12 }}>
            Web Player URL
          </div>
          <img src={qr(webPlayerUrl)} alt="Web Player QR"
            style={{ width: 200, height: 200, margin: "0 auto", display: "block" }} />
          <div style={{ fontFamily: "monospace", fontSize: 11, marginTop: 8,
            wordBreak: "break-all", color: "#475569" }}>
            {shortUrl}
          </div>
          <div style={{ fontSize: 11, color: "#64748b", marginTop: 6 }}>
            Scan with TV browser or type the short URL.
          </div>
        </div>

        <div className="print-card" style={{ textAlign: "center" }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#64748b",
            textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 12 }}>
            Android APK Download
          </div>
          <img src={qr(apkUrl)} alt="APK Download QR"
            style={{ width: 200, height: 200, margin: "0 auto", display: "block" }} />
          <div style={{ fontFamily: "monospace", fontSize: 11, marginTop: 8,
            wordBreak: "break-all", color: "#475569" }}>
            {apkUrl}
          </div>
          <div style={{ fontSize: 11, color: "#64748b", marginTop: 6 }}>
            Scan with Fire TV / T95 / Android TV browser to fetch the APK.
          </div>
        </div>
      </div>

      {/* Android setup steps */}
      <div className="print-card" style={{ marginTop: 16 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: "#64748b",
          textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 12 }}>
          Android Player Setup
        </div>
        <ol style={{ marginTop: 0, paddingLeft: 20, fontSize: 13, lineHeight: 1.7 }}>
          <li>Download &amp; install the APK from the link above (or use Fire TV "Downloader").</li>
          <li>Open VM Display Player from the launcher.</li>
          <li>On Setup, enter Device Code: <strong style={{ fontFamily: "monospace", fontSize: 15 }}>{d.device_code}</strong></li>
          <li>Tap Connect. The player auto-launches on every boot.</li>
        </ol>
      </div>

      {/* Web Player setup steps */}
      <div className="print-card">
        <div style={{ fontSize: 12, fontWeight: 700, color: "#64748b",
          textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 12 }}>
          Browser / Smart TV Setup
        </div>
        <ol style={{ marginTop: 0, paddingLeft: 20, fontSize: 13, lineHeight: 1.7 }}>
          <li>On the TV browser, navigate to: <code style={{ fontSize: 13 }}>{webPlayerUrl}</code></li>
          <li>Press F11 (or use the TV's fullscreen / kiosk mode).</li>
          <li>Set as homepage so it loads on every boot.</li>
        </ol>
      </div>

      {/* Support */}
      <div style={{
        marginTop: 16, padding: 16, borderRadius: 8,
        background: "linear-gradient(135deg, rgba(212,175,55,0.06) 0%, transparent 100%)",
        border: "1px solid rgba(212,175,55,0.3)", fontSize: 12, color: "#475569",
      }}>
        <strong>Need help?</strong> Contact your VM Display administrator and
        quote device code <strong style={{ fontFamily: "monospace" }}>{d.device_code}</strong>.
        Generated {new Date().toLocaleString()}.
      </div>
    </div>
  );
};

const Empty: React.FC<{ msg: string }> = ({ msg }) => (
  <div style={{
    minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
    fontFamily: '-apple-system, sans-serif', color: "#64748b",
  }}>
    {msg}
  </div>
);

export default PrintSheet;
